import * as React from 'react'
import {ChangeEvent, Component, ReactElement} from 'react'
import {Location} from 'react-router-dom'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {Box} from '@mui/material';
import {StyledPageTitle} from '../../../components/reservations/components/CommonStyle'
import styled from 'styled-components'
import {Colors, FontSize, FontWeights} from '../../../styledComponents/Constants'
import {
    BlockSpacer,
    BlockSpacerSpOnly,
    currentMedia,
    GridContainer,
    GridItem
} from '../../../styledComponents/ResponsiveConvenience'
import {
    DisplayItemDividerForPageTop,
    DisplayItemInputTitleRequiredNoMargin
} from '../../../styledComponents/DisplayItem'
import {PlainLinkStyledBackButton, PrimaryStyledButton, StyledSecondaryButton} from '../../../styledComponents/Button'
import NatResidentUserRepo from '../residentUser/repository/NatResidentUserRepo'
import NatAmountFixRepo from './repository/NatAmountFixRepo'
import PPRadioGroupForResident from '../../../components/shared/forms/resident/PPRadioGroupForResident'
import {HONORIFICS, LOGIN_MODE, MESSAGE_TYPE, RECEIPT_ISSUE_STATUS} from '../../../CodeMaster'
import {Messages} from '../../../Messages'
import NatResidentMessageDialogComponent from '../common/resident/NatResidentMessageDialogComponent'
import {ListGridContainer} from '../../../styledComponents/GridComponents'
import {NatPutReceiptIssueRequest} from './repository/NatGetReceiptIssuePrevInputInfo'
import {
    HeadingAreaMessageComponentForResident
} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {getFormattedYearMonth} from '../common/NatDateFormatted'
import {Nullable} from '../../../app/TypeHelpers'
import PPTextWithPullDownForResident from '../../../components/shared/forms/resident/PPTextWithPullDownForResident'
import NatResidentReceiptIssueProviso from './NatResidentReceiptIssueProviso'
import {VariableTrading} from './repository/NatGetResidentBillingAmountDetail'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {ResidentLoginInfoSessionStorageKey} from '../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatBaseResponseInterface} from '../common/repository/NatBaseResponseInterface';
import PreformattedDiv from '../../../styledComponents/PreformattedDiv';
import {WideMainContainer} from '../../../styledComponents/ContentsArea';

export interface BillingAmountLocationState {
    ridgeCd: string,
    roomNumber: string,
    seq: number,
    issueYearMonth: string,
    variableTrading: VariableTrading[],
    receiptIssueStatus: string,
    mainUserName: string,
    haveAuthIssueReceipt: boolean,
}

interface Props {
    condominiumCd: string,
    residentUserRepo: NatResidentUserRepo,
    amountFixRepo: NatAmountFixRepo,
    location: Location
}

interface State {
    destination: string,
    honorific: string,
    destinationPrev: Nullable<string>,
    honorificPrev: string,
    checkedItemName: string[],
    openPullDown: boolean,
    openDialog: boolean,
    issueButtonDisabled: boolean,
    isLoadCompleted: boolean,
    maintenanceMessage: string
}

type VariableTradingItem = {
    expenseItemName: string;
    amount: string;
    summaryCode: string;
    accountingYearMonth: string;
};

class NatResidentReceiptIssuePage
    extends Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {
    isAdminUser: boolean

    constructor(props: Readonly<Props & ResidentWithErrorHandlingPagePropsSet>) {
        super(props)
        this.state = {
            destination: '',
            honorific: '',
            destinationPrev: null,
            honorificPrev: '',
            checkedItemName: [],
            openPullDown: false,
            openDialog: false,
            issueButtonDisabled: true,
            isLoadCompleted: false,
            maintenanceMessage: ''
        }
        this.isAdminUser = SessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_MODE) === LOGIN_MODE.ADMIN.value
        if (!this.props.location.state) {
            this.props.navigate(
                `/resident/error`,
            );
            return;
        }
        this.backButtonClick = this.backButtonClick.bind(this)
        this.issueButtonClick = this.issueButtonClick.bind(this)
        this.handleChangeOkClick = this.handleChangeOkClick.bind(this)
        this.handleCancelClick = this.handleCancelClick.bind(this)
        this.handleClickPullDownItem = this.handleClickPullDownItem.bind(this)
        this.handleChangeText = this.handleChangeText.bind(this)
        this.handleChangeRadio = this.handleChangeRadio.bind(this)
        this.onChangeToggleAll = this.onChangeToggleAll.bind(this)
        this.onChangeToggleItem = this.onChangeToggleItem.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {amountFixRepo, condominiumCd, eventEmitter} = this.props
        const {ridgeCd, roomNumber, seq, haveAuthIssueReceipt} = this.props.location.state
        await this.props.eventEmitter.showLoading();
        try {
            if (haveAuthIssueReceipt && !this.isAdminUser) {
                // 領収書発行（前回分）取得
                const billingAmountDetail = await this.props.asyncCallFunction(
                    this.props.amountFixRepo.getReceiptIssuePrevInput.bind(
                        amountFixRepo,
                        condominiumCd,
                        ridgeCd,
                        roomNumber,
                        seq
                    ), (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    }
                )

                if (billingAmountDetail?.details) {
                    this.setState({
                        destinationPrev: billingAmountDetail.details.destination,
                        honorificPrev: billingAmountDetail.details.honorific,
                    })
                }
            }
        } finally {
            this.props.eventEmitter.closeLoading();
            this.setState({
                isLoadCompleted: true
            })
        }
    }

    // 領収書発行ボタンの活性制御
    updateIssueButtonDisabled(): void {
        const {destination, honorific, checkedItemName} = this.state

        let issueButtonDisabled = false
        if (!destination || !honorific || checkedItemName.length === 0) {
            issueButtonDisabled = true
        }

        this.setState({issueButtonDisabled: issueButtonDisabled})
    }

    // 戻るボタン
    backButtonClick(): void {
        const {navigate, condominiumCd} = this.props
        const locationState = this.props.location.state
        navigate(`/resident/condominiums/${condominiumCd}/billingAmountConfirm`,
            {
                state: JSON.parse(JSON.stringify(locationState))
            }
        )
    }

    // 領収書発行ボタン
    async issueButtonClick(): Promise<void> {
        let preProcessResponse
        try {
            if (!this.isAdminUser) {
                preProcessResponse = await this.preProcessingIssueReceipt()
            }
        } finally {
            if (this.isAdminUser || preProcessResponse !== null) {
                this.setState({
                    openDialog: true
                })
            }
        }
    }

    // 領収書を発行する
    async handleChangeOkClick(): Promise<void> {
        try {
            await this.downloadIssueReceipt()
        } finally {
            this.setState({
                openDialog: false
            })
        }
    }

    async preProcessingIssueReceipt(): Promise<Promise<NatBaseResponseInterface | null> | null> {
        const {roomNumber, ridgeCd, seq, issueYearMonth} = this.props.location.state
        const {condominiumCd} = this.props
        const {destination, honorific, checkedItemName} = this.state

        await this.props.eventEmitter.showLoading()
        const {variableTrading} = this.props.location.state as {
            variableTrading: VariableTradingItem[];
        };
        try {
            const request: NatPutReceiptIssueRequest = {
                condominiumCd: condominiumCd,
                roomNumber: roomNumber,
                ridgeCd: ridgeCd,
                seq: seq,
                issueTargetYearMonth: issueYearMonth,
                destination: destination,
                honorific: honorific,
                proviso: variableTrading.filter((item) => checkedItemName.includes(item.expenseItemName)),
                provisoAllSelectFlag: checkedItemName.length === variableTrading.length ? '1' : '0'
            }
            // 領収書発行（事前処理）
            const response = await this.props.asyncCallFunction(
                this.props.amountFixRepo.postIssueReceiptPreProcessing.bind(
                    this.props.amountFixRepo,
                    request
                ), async (httpErrorOrNetworkError) => {
                    if (httpErrorOrNetworkError.responseBody.requestError.messageId === Messages.ESYCC0027.id) {
                        this.setState({
                            maintenanceMessage: Messages.ESYCC0027.message
                        })
                    } else {
                        await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                    }
                },
                {
                    defaultErrorTitle: Messages.EOPCR0014.toString(),
                    defaultErrorTitlePriority: true,
                    replacementDetailErrorMessages: [{
                        targetMessageId: Messages.EOPCA0004,
                        replacementMessageId: Messages.EOPCR0015
                    }]
                },
            )

            return await response ? response : null
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async downloadIssueReceipt(): Promise<void> {
        const {roomNumber, ridgeCd, seq} = this.props.location.state
        const {condominiumCd} = this.props

        const isSutekiNetSmartPhoneApplication = SessionStorageHelper
            .getStringItemWithDefault('isSutekiNetSmartPhoneApplication', 'false')

        // 領収書ダウンロード
        const tempLink: HTMLAnchorElement = document.createElement('a')
        tempLink.href = `/api/resident/condominiums/${condominiumCd}/ridges/${ridgeCd}/rooms/${roomNumber}/receipt/user/${seq}/issue`
        if (isSutekiNetSmartPhoneApplication === 'true') {
            tempLink.target = '_blank'
        }
        document.body.appendChild(tempLink)
        tempLink.click()
        document.body.removeChild(tempLink)
    }

    // ダイアログのキャンセル
    handleCancelClick(): void {
        this.setState({openDialog: false})
    }

    // プルダウンの履歴を押下
    handleClickPullDownItem = (item: string) => {
        this.setState({
            destination: item,
            honorific: this.state.honorificPrev,
        }, () => {
            this.updateIssueButtonDisabled()
        })
    }

    // テキスト項目更新
    handleChangeText(inputValue: string): void {
        this.setState({destination: inputValue}, () => {
            this.updateIssueButtonDisabled()
        })
    }

    // ラジオ項目更新
    handleChangeRadio(event: ChangeEvent<HTMLInputElement>): void {
        this.setState({honorific: event.target.value}, () => {
            this.updateIssueButtonDisabled()
        })
    }

    // 但し書き：すべて選択
    onChangeToggleAll(checkedItems: string[]): void {
        this.setState({
            checkedItemName: checkedItems
        }, () => {
            this.updateIssueButtonDisabled()
        })
    }

    // 但し書き：選択
    onChangeToggleItem(checkedItems: string[]): void {
        this.setState({
            checkedItemName: checkedItems
        }, () => {
            this.updateIssueButtonDisabled()
        })
    };

    // 選択した但し書きの合計
    calculateTotal = (): number => {
        const {checkedItemName} = this.state
        const {variableTrading} = this.props.location.state as {
            variableTrading: VariableTradingItem[];
        };

        return variableTrading
            ? variableTrading.filter((item) => checkedItemName.includes(item.expenseItemName))
                .reduce((total, item) => total + Number(item.amount), 0)
            : 0
    };

    renderDialog(): ReactElement {
        const {destination, honorific, checkedItemName} = this.state

        const displayItem = checkedItemName.slice(0, 2).join(' , ')
        const message: string =
            this.isAdminUser
                ? Messages.ISYCR0001.toString()
                : Messages.WNTFR0001.toString(
                    destination + (honorific === HONORIFICS.CORPORATION.value ? '御中' : '様'),
                    checkedItemName.length > 2 ? displayItem + ' 他' : displayItem,
                    this.calculateTotal().toLocaleString() + '円'
                )
        return (
            this.isAdminUser
                ? <NatResidentMessageDialogComponent
                    open={this.state.openDialog}
                    params={{
                        title: '確認',
                        errorLevel: MESSAGE_TYPE.INFORMATION,
                        message: message,
                        buttonProps: [
                            {
                                isDefault: false,
                                buttonLabel: 'OK',
                                buttonHandler: this.handleCancelClick,
                            },
                        ],
                    }}
                />
                : <NatResidentMessageDialogComponent
                    open={this.state.openDialog}
                    params={{
                        title: '確認',
                        errorLevel: MESSAGE_TYPE.WARNING,
                        message: message,
                        buttonProps: [
                            {
                                isDefault: false,
                                buttonLabel: 'キャンセル',
                                buttonHandler: this.handleCancelClick,
                            },
                            {
                                isDefault: false,
                                buttonLabel: '発行する',
                                buttonHandler: this.handleChangeOkClick,
                            },
                        ],
                    }}
                />
        )
    }

    renderIssueMonthArea(): ReactElement {
        const {issueYearMonth, receiptIssueStatus} = this.props.location.state
        return (<>
                <ItemHeading>
                    発行月
                </ItemHeading>
                <WhiteArea>
                    <BlockSpacer height={'5px'}/>
                    {this.isAdminUser
                        ? issueYearMonth
                        : getFormattedYearMonth(issueYearMonth)}分<br/>
                    {RECEIPT_ISSUE_STATUS.codeToEnumConstants(receiptIssueStatus).label}
                    <BlockSpacer height={'5px'}/>
                </WhiteArea>
            </>
        )
    }

    renderReceiptDestinationArea(): ReactElement {
        const {destinationPrev, honorific} = this.state

        return (<>
                {<>
                    <ItemHeading>
                        領収書宛先
                    </ItemHeading>
                    <WhiteArea>
                        <GridContainer>
                            <GridItem over={3}>
                                {
                                    <DisplayItemInputTitleRequiredNoMargin>
                                        {'宛先'}
                                    </DisplayItemInputTitleRequiredNoMargin>
                                }
                            </GridItem>
                            <GridItem over={9}>
                                <BlockSpacerSpOnly height={'10px'}/>
                                <PPTextWithPullDownForResident
                                    defaultValue={''}
                                    inputMaxLength={27}
                                    inputPlaceholder={'例：素敵株式会社、素敵 太郎など'}
                                    pullDownItems={destinationPrev ? [destinationPrev] : null}
                                    handleChangeText={this.handleChangeText}
                                    handleClickPullDownItem={this.handleClickPullDownItem}
                                    eventKeys={'destination'}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </GridItem>
                        </GridContainer>
                        <Box height={'15px'}/>
                        <GridContainer>
                            <GridItem over={3}>
                                {
                                    <DisplayItemInputTitleRequiredNoMargin>
                                        {'敬称'}
                                    </DisplayItemInputTitleRequiredNoMargin>
                                }
                            </GridItem>
                            <GridItem over={9}>
                                <BlockSpacerSpOnly height={'10px'}/>
                                <PPRadioGroupForResident
                                    data={HONORIFICS.values()}
                                    selectValue={honorific}
                                    onChange={this.handleChangeRadio}
                                    eventKeys={['honorific']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </GridItem>
                        </GridContainer>
                    </WhiteArea>
                </>}
            </>
        )
    }

    renderProvisoSelectionArea(): ReactElement {
        const {variableTrading} = this.props.location.state
        return (<>
                <ItemHeading>
                    但し書き選択
                </ItemHeading>
                <NatResidentReceiptIssueProviso
                    variableTrading={variableTrading}
                    isAdminUser={this.isAdminUser}
                    onChangeToggleAll={this.onChangeToggleAll}
                    onChangeToggleItem={this.onChangeToggleItem}
                    eventKeys={['proviso']}
                    eventEmitter={this.props.eventEmitter}
                />
            </>
        )
    }

    renderHaveAuthIssueReceipt(): Nullable<ReactElement> {
        return (<>
                <BlockSpacer height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                {this.renderIssueMonthArea()}
                <BlockSpacer height={'15px'}/>
                {this.renderReceiptDestinationArea()}
                <BlockSpacer height={'15px'}/>
                {this.renderProvisoSelectionArea()}
                <BlockSpacer height={'20px'}/>
                <PrimaryStyledButton
                    disabled={this.state.issueButtonDisabled}
                    onClick={this.issueButtonClick}>
                    領収書を発行する
                </PrimaryStyledButton>
                <BlockSpacer height={'30px'}/>
                <StyledSecondaryButton onClick={this.backButtonClick}>
                    戻る
                </StyledSecondaryButton>
                <BlockSpacer height={'30px'}/>
                <ListGridContainer fontSize={FontSize.Middle}>
                    <div>・</div>
                    <div>お取引月ごとに領収書の発行ができます。</div>
                    <div>・</div>
                    <div>発行月に対する2回目以降の発行は、宛先や項目が異なっていても再発行扱いとなります。</div>
                </ListGridContainer>
            </>
        )
    }

    renderNotHaveAuthIssueReceipt(): Nullable<ReactElement> {
        const {mainUserName} = this.props.location.state
        return (<>
                <Box fontWeight={FontWeights.Bold} color={Colors.Alert}>
                    本機能はご利用いただけません。<br/>
                    ご利用になる場合は、メインユーザの{mainUserName}さんにご確認ください。<br/>
                    メインユーザアカウントの「個人設定＞ユーザ管理」から利用制限の設定を変更できます。
                </Box>
                <BlockSpacer height={'20px'}/>
            </>
        )
    }

    renderMaintenanceMassageArea(): Nullable<ReactElement> {
        const {maintenanceMessage} = this.state
        return (
            <WhiteMessageArea>
                <PreformattedDiv>{maintenanceMessage}</PreformattedDiv>
            </WhiteMessageArea>
        )
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return <></>
        }

        return (<>
            {this.renderDialog()}
            <PlainLinkStyledBackButton onClick={this.backButtonClick}>
                戻る
            </PlainLinkStyledBackButton>
            <BlockSpacer height={'15px'}/>
            <DisplayItemDividerForPageTop/>
            <BlockSpacer height={'30px'}/>
            <StyledPageTitle>
                領収書発行
            </StyledPageTitle>
            <BlockSpacer height={'20px'}/>
            {!this.props.location.state.haveAuthIssueReceipt && this.renderNotHaveAuthIssueReceipt()}
            選択した但し書きで領収書を発行できます<br/>
            一度発行すると新規発行はできません
            {this.props.location.state.haveAuthIssueReceipt &&
                <>
                    {this.state.maintenanceMessage ?
                        <>
                            <BlockSpacer height={'20px'}/>
                            {this.renderMaintenanceMassageArea()}
                            <Box height={'20px'}/>
                            <StyledSecondaryButton
                                onClick={() => this.props.navigate({
                                    pathname: '/resident/mypage',
                                })}>
                                ホームへ戻る
                            </StyledSecondaryButton>
                        </>
                        :
                        <>
                            {this.renderHaveAuthIssueReceipt()}
                            <BlockSpacer height={'15px'}/>
                        </>
                    }
                </>
            }
        </>)
    }
}

export default createResidentWithErrorHandlingPage(NatResidentReceiptIssuePage)

const WhiteArea = styled.div`
    background-color: ${Colors.Surface};
    ${currentMedia.lessThan('sp')`
    width: calc(100% + 30px);
    margin: 0px -15px;
  `};
    padding: 15px;
    box-sizing: border-box;
    overflow: hidden;
`

const ItemHeading = styled.div`
    background-color: ${Colors.AccentLight};
    ${currentMedia.lessThan('sp')`
    width: calc(100% + 30px);
    margin: 0px -15px;
    margin-top: 5px;
  `};
    font-size: ${FontSize.Body};
    padding: 6px 15px 6px;
    box-sizing: border-box;
    overflow: hidden;
`

const WhiteMessageArea = styled(WideMainContainer)`
    padding: 15px;
`
